import React from 'react'
import { useApolloClient } from '@apollo/client'
import { Modal, Button } from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentBasePath } from 'cg/components/documents/useDocumentBasePath'
import gql from 'graphql-tag'
import PropTypes from 'prop-types'

const DeleteSectionModal = ({ nodesLength, section, mutation, callback, removeNestedIds, activeTabId }) => {
  const client = useApolloClient()

  const { id, tabId, itemType, title, descendantGroupIds } = section
  const { versionId, sectionId: activeSectionId } = useParams()
  const navigate = useNavigate()
  const documentBasePath = useDocumentBasePath()
  let itemTypeLabel = 'Section'
  let warningText = 'All section content will be removed.'
  if (itemType === 'group') {
    itemTypeLabel = 'Group'
    warningText = `All of the group's sections and content will be removed.`
  } else if (itemType === 'tab') {
    itemTypeLabel = 'Tab'
    warningText = `All groups and sections in this tab and their content will be removed.`
  }

  const handleMutationCompleted = data => {
    const { deleteDocumentSection } = data

    const { ascendantIds } =
      (activeSectionId &&
        activeSectionId !== '0' &&
        client.readFragment({
          id: `Section:${activeSectionId}`,
          fragment: gql`
            fragment Section on Section {
              id
              ascendantIds
            }
          `
        })) ||
      {}

    const { previousSection: prevSection, nextSection, id } = deleteDocumentSection
    const changeNavigation = (prevSection, nextSection, tabAware = true) => {
      const getPath = section =>
        `${documentBasePath}/editor/${versionId}/${section ? section.id : '0'}`

      if (tabAware) {
        if (prevSection?.tabId === tabId || nextSection?.tabId === tabId) {
          return navigate(getPath(prevSection?.tabId === tabId ? prevSection : nextSection))
        }
      }

      return navigate(getPath(prevSection || nextSection))
    }
    switch (itemType) {
      case 'section':
        // Check if you are deleting the currently active section
        if (activeSectionId === id) {
          return changeNavigation(prevSection, nextSection)
        }
        break
      case 'group':
        // Check if you are deleting a group containing the currently active section
        if (ascendantIds && ascendantIds.includes(id)) {
          return changeNavigation(prevSection, nextSection)
        }
        break
      default:
        // Deleting the active tab, or a section/group inside the tab being deleted
        if (activeTabId === id || (ascendantIds && ascendantIds.includes(id))) {
          return changeNavigation(prevSection, nextSection, false)
        }
        break
    }
  }

  const handleDelete = async () => {
    callback(false)

    if (itemType === 'group' || itemType === 'tab') {
      removeNestedIds?.([id].concat(descendantGroupIds || []))
    }

    await mutation.mutateOptimistic({
      variables: { id },
      onCompleted: handleMutationCompleted
    })

    if (nodesLength === 1) {
      navigate(`${documentBasePath}/editor/${versionId}/0`)
    }
  }

  return (
    <Modal
      show
      onHide={() => {
        callback(false)
      }}
    >
      <Modal.Header>
        {id && (
          <Modal.Title>
            <strong>Delete</strong> <span>{itemTypeLabel}: </span>
            {title}
          </Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body>{warningText}</Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={handleDelete}>
          Delete {itemTypeLabel}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            callback(false)
          }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

DeleteSectionModal.propTypes = {
  nodesLength: PropTypes.number,
  section: PropTypes.shape({
    id: PropTypes.string,
    tabId: PropTypes.string,
    nextItemId: PropTypes.string,
    title: PropTypes.string,
    itemType: PropTypes.oneOf(['group', 'section', 'tab']),
    depth: PropTypes.number,
    icon: PropTypes.string,
    color: PropTypes.string,
    documentVersionId: PropTypes.string,
    groupId: PropTypes.string,
    descendantGroupIds: PropTypes.arrayOf(PropTypes.string),
    descendantIds: PropTypes.arrayOf(PropTypes.string),
    path: PropTypes.string,
    firstSection: PropTypes.shape({
      id: PropTypes.string
    }),
    excludeFromPublish: PropTypes.bool
  }),
  mutation: PropTypes.shape({
    error: PropTypes.any,
    mutateOptimistic: PropTypes.func,
    mutate: PropTypes.func
  }),
  callback: PropTypes.func,
  removeNestedIds: PropTypes.func,
  activeTabId: PropTypes.string
}

export default DeleteSectionModal
